import type { ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight, Database, FolderSearch, Lock, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

/** Tarjeta de sección del portal (estilo boceto: blanca, borde suave, título + acción). */
export function SectionCard({
  title,
  action,
  children,
  className,
  bodyClassName,
}: {
  title?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
  bodyClassName?: string;
}) {
  return (
    <section className={cn("rounded-xl border border-border bg-card shadow-[0_1px_2px_rgba(16,24,40,0.04)]", className)}>
      {(title || action) && (
        <header className="flex items-center justify-between gap-3 px-5 pt-5">
          {title && <h2 className="text-[15px] font-semibold tracking-tight text-foreground">{title}</h2>}
          {action}
        </header>
      )}
      <div className={cn("p-5", bodyClassName)}>{children}</div>
    </section>
  );
}

export function EmptyState({
  icon: Icon = FolderSearch,
  title,
  description,
  className,
  children,
}: {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  className?: string;
  children?: ReactNode;
}) {
  return (
    <div className={cn("flex flex-col items-center justify-center px-6 py-10 text-center", className)}>
      <div className="mb-3 flex size-11 items-center justify-center rounded-full bg-primary/10 text-primary">
        <Icon className="size-5" />
      </div>
      <p className="text-sm font-medium text-foreground">{title}</p>
      {description && <p className="mt-1 max-w-sm text-[13px] text-muted-foreground">{description}</p>}
      {children && <div className="mt-4">{children}</div>}
    </div>
  );
}

/** La RPC/tabla todavía no está desplegada en esta BD. */
export function PendienteBD({ what, className }: { what?: string; className?: string }) {
  return (
    <EmptyState
      icon={Database}
      title="Pendiente de configuración en BD"
      description={
        what
          ? `${what} estará disponible en cuanto se aplique la migración del Portal Socio.`
          : "Esta sección estará disponible en cuanto se aplique la migración del Portal Socio."
      }
      className={className}
    />
  );
}

export function SinAcceso({ className }: { className?: string }) {
  return (
    <EmptyState
      icon={Lock}
      title="Sin acceso a este proyecto"
      description="Tu usuario no tiene una asignación activa en este desarrollo. Si crees que es un error, contacta a SOZU."
      className={className}
    />
  );
}

export function SinProyectos() {
  return (
    <SectionCard>
      <EmptyState
        title="Aún no tienes proyectos asignados"
        description="Cuando tu asignación como socio quede activa, aquí verás el avance y desempeño de tu desarrollo."
      />
    </SectionCard>
  );
}

/** Δ con flecha y color. `invert` para métricas donde bajar es bueno. */
export function Delta({ value, suffix = "", unit = "%", className, invert = false }: {
  value: number | null | undefined;
  suffix?: string;
  /** Unidad tras el número ("%" por defecto; " pts" para puntos porcentuales). */
  unit?: string;
  className?: string;
  invert?: boolean;
}) {
  if (value == null || !Number.isFinite(value)) return null;
  const up = value >= 0;
  const good = invert ? !up : up;
  const Icon = up ? ArrowUpRight : ArrowDownRight;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-0.5 text-[12px] font-semibold tabular-nums",
        good ? "text-primary" : "text-destructive",
        className,
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      {up ? "+" : ""}
      {value.toLocaleString("es-MX", { maximumFractionDigits: 0 })}
      {unit}
      {suffix}
    </span>
  );
}

export function ProgressBar({ value, className }: { value: number | null | undefined; className?: string }) {
  const v = Math.max(0, Math.min(100, value ?? 0));
  return (
    <div
      className={cn("h-2 w-full overflow-hidden rounded-full bg-muted", className)}
      role="progressbar"
      aria-valuenow={Math.round(v)}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${v}%` }} />
    </div>
  );
}

export function PageHeader({
  title,
  subtitle,
  aside,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight text-foreground sm:text-[28px]">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {aside}
    </div>
  );
}
